import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { twMerge } from "tailwind-merge";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { useVisibleViewPort } from "shared/context/VisibleViewPortContext";

// todo: we can depricate this over time
export enum ModalMaxWidth {
  Sm_500 = "max-w-[500px] ",
  Md_700 = "max-w-[700px] ",
  Lg_900 = "max-w-[900px] ",
  Xl_1200 = "max-w-[1200px] ",
  Full = "w-full ",
  Responsive = "w-full max-w-[1500px] mx-[15%] ",
}
export enum ModalMinWidth {
  Sm_500 = "min-w-[500px] ",
  Md_700 = "min-w-[700px] ",
  Lg_900 = "min-w-[900px] ",
  Xl_1200 = "min-w-[1200px] ",
  Full = "min-w-full ",
}
export enum ModalMinHeight {
  Sm_200 = "min-h-[200px] ",
  Md_500 = "min-h-[500px] ",
  Lg_735 = "min-h-[735px] ",
  Full = "min-h-screen ",
}
export enum ModalMaxHeight {
  Sm_200 = "max-h-[200px] ",
  Md_500 = "max-h-[500px] ",
  Lg_735 = "max-h-[735px] ",
  Full = "max-h-screen ",
}

export type PillarModalBodyProps<ControlRequiredGeneric> = {
  containerClasses?: string;
  title?: string;
  subtitle?: string;
  testid?: string;
  maxW?: string;
  minW?: string;
  maxH?: string;
  minH?: string;
  className?: string;
  children?: React.ReactNode;
  dialogPanelWrapperClasses?: string;
  control: ControlRequiredGeneric extends true
    ? PillarModalControl
    : Omit<PillarModalControl, "setIsOpen" | "onClose"> & {
        setIsOpen?: PillarModalControl["setIsOpen"];
        onClose?: PillarModalControl["onClose"];
      };
};

const PillarModalBody = <ControlRequiredGeneric extends boolean = false>({
  control,
  containerClasses,
  maxW,
  minW,
  maxH,
  minH,
  className,
  children,
  testid,
  title,
  subtitle,
  dialogPanelWrapperClasses,
  ...props
}: PillarModalBodyProps<ControlRequiredGeneric>) => {
  const { isOpen, onClose } = control;
  // When embedded in the SSO iframe, the iframe is stretched to the full page
  // height and never scrolls itself, so a `fixed inset-0` overlay centers in
  // the whole document instead of the user's visible viewport. Anchor the
  // overlay to the slice the parent reports as visible (null when not embedded).
  const visibleViewPort = useVisibleViewPort();
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={twMerge(
          "fixed inset-0 flex justify-center items-center overflow-auto z-50",
          containerClasses,
        )}
        style={
          visibleViewPort
            ? {
                position: "absolute",
                top: visibleViewPort.top,
                height: visibleViewPort.height,
                bottom: "auto",
              }
            : undefined
        }
        onClose={() => onClose?.()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-25"
          leave="ease-in duration-200"
          leaveFrom="opacity-25"
          leaveTo="opacity-0"
        >
          <div className="appmodal-body-background fixed inset-0" />
        </Transition.Child>
        <div className="relative max-h-full">
          <div
            className={twMerge(
              "px-3 flex min-h-full items-center justify-center",
              dialogPanelWrapperClasses,
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  "appmodal-panel-container transform transition-all",
                  maxW,
                  minW,
                  minH,
                  maxH,
                  className,
                )}
                data-testid={testid}
                {...props}
              >
                <Dialog.Title
                  as="h3"
                  data-testid={`${testid}-title`}
                  className="appmodal-panel-title flex justify-center"
                >
                  {title}
                </Dialog.Title>
                {subtitle && <h6 className="text-subtle mb-2">{subtitle}</h6>}
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PillarModalBody;
